import type { Product as TrackingProduct } from "@telenornorgeinternal/telenor-norway-tracking";

export const entertainmentTracking = {
	pricePerPointPackage: 29,
	channelChanges: ref<TrackingProduct[]>([]),
	pointPackage: ref<TrackingProduct | null>(null),
	premiumTvContent: ref<TrackingProduct | null>(null),

	/**
	 * Channel changes are tracked as service.change events once the user checks out.
	 * No cart, checkout or purchase events are tracked for channel changes.
	 */
	channelChange: (
		channel: TrackingProduct,
		totalPointPackageQuantity: number = 0,
		removeFromTracking: boolean = false,
	) => {
		try {
			entertainmentTracking.setPointPackageInCart(totalPointPackageQuantity);

			if (removeFromTracking) {
				entertainmentTracking.channelChanges.value = entertainmentTracking.channelChanges.value.filter(
					(c) => c.id !== channel.id,
				);
				return;
			}
			entertainmentTracking.channelChanges.value.push(channel);
		} catch (err) {
			console.error("Error tracking channel change:", err);
		}
	},

	addPremiumChannelToCart: (productInfo: TrackingProduct) => {
		try {
			entertainmentTracking.premiumTvContent.value = productInfo;
			useAnalytics().cart.add({
				product: productInfo,
			});
		} catch (err) {
			console.error("Error tracking add channel to cart:", err);
		}
	},

	/**
	 * Track checkout with point packages
	 */
	trackCheckout: () => {
		try {
			if (!entertainmentTracking.pointPackage.value) {
				return;
			}
			useAnalytics().order.begin({
				products: [entertainmentTracking.pointPackage.value],
			});
		} catch (err) {
			console.error("Error tracking checkout:", err);
		}
	},

	/**
	 * Track premium checkout with premium TV content
	 */
	trackPremiumCheckout: () => {
		try {
			if (!entertainmentTracking.premiumTvContent.value) {
				return;
			}
			useAnalytics().order.begin({
				products: [entertainmentTracking.premiumTvContent.value],
			});
		} catch (err) {
			console.error("Error tracking premium checkout:", err);
		}
	},

	/**
	 * Track channel changes as a service change event
	 */
	trackPointShuffle: () => {
		try {
			if (entertainmentTracking.channelChanges.value.length === 0) {
				return;
			}
			useAnalytics().service.change({
				name: "point_shuffle",
				products: entertainmentTracking.channelChanges.value,
			});
			entertainmentTracking.channelChanges.value = [];
		} catch (err) {
			console.error("Error tracking point shuffle:", err);
		}
	},

	/**
	 * Track purchase that includes tv content and/or point packages.
	 * If removing point packages issue a terminate event.
	 */
	trackPurchase: () => {
		try {
			entertainmentTracking.trackPointShuffle();
			if (!entertainmentTracking.pointPackage.value) {
				return;
			}

			if (entertainmentTracking.pointPackage.value.quantity < 0) {
				useAnalytics().order.terminate({
					products: [
						{
							...entertainmentTracking.pointPackage.value,
							quantity: Math.abs(entertainmentTracking.pointPackage.value.quantity),
						},
					],
				});
			} else {
				useAnalytics().order.complete({
					orderId: new Date().getTime().toString(), // Using timestamp no order ID exists
					deliveryMethod: "digital",
					paymentType: "subscription",
					products: [entertainmentTracking.pointPackage.value],
				});
			}

			entertainmentTracking.pointPackage.value = null;
		} catch (err) {
			console.error("Error tracking purchase:", err);
		}
	},

	trackPremiumPurchase: () => {
		try {
			if (!entertainmentTracking.premiumTvContent.value) {
				return;
			}

			if (entertainmentTracking.premiumTvContent.value.quantity < 0) {
				useAnalytics().order.terminate({
					products: [
						{
							...entertainmentTracking.premiumTvContent.value,
							quantity: Math.abs(entertainmentTracking.premiumTvContent.value.quantity),
						},
					],
				});
			} else {
				useAnalytics().order.complete({
					orderId: new Date().getTime().toString(),
					deliveryMethod: "digital",
					paymentType: "subscription",
					products: [entertainmentTracking.premiumTvContent.value],
				});
			}

			entertainmentTracking.premiumTvContent.value = null;
		} catch (err) {
			console.error("Error tracking premium purchase:", err);
		}
	},

	setPointPackageInCart: (pointPackageQuantity: number) => {
		try {
			const subcategoryName = pointPackageQuantity > 0 ? "Add" : "Remove";
			if (entertainmentTracking.pointPackage.value) {
				useAnalytics().cart.remove({
					product: entertainmentTracking.pointPackage.value,
				});
				entertainmentTracking.pointPackage.value = null;
			}

			if (pointPackageQuantity !== 0) {
				const pointPackageProduct: TrackingProduct = {
					productArea: EntertainmentTrackingConstants.PRODUCT_AREA,
					categoryName: EntertainmentTrackingCategories.POINT_PACKAGE,
					subCategoryName: subcategoryName,
					id: "8976",
					name: `Point Package`,
					price: entertainmentTracking.pricePerPointPackage,
					quantity: pointPackageQuantity,
				};
				entertainmentTracking.pointPackage.value = pointPackageProduct;
				useAnalytics().cart.add({
					product: pointPackageProduct,
				});
			}
		} catch (err) {
			console.error("Error setting point packages in cart:", err);
		}
	},

	navigated: (from?: string, to?: string) => {
		if (!from || !to) return;
		if (from.includes("/twe/innholds-velger") && !to.includes("/twe/innholds-velger")) {
			entertainmentTracking.reset();
		}
	},

	reset: () => {
		entertainmentTracking.channelChanges.value = [];
		entertainmentTracking.pointPackage.value = null;
		entertainmentTracking.premiumTvContent.value = null;
	},
};

export interface ChannelProductInfo {
	id: string;
	name: string;
	pointCost: number;
	subCategory: "channel" | "streaming";
	quantity?: number;
}

export enum EntertainmentTrackingConstants {
	PRODUCT_AREA = "Entertainment",
}

export enum EntertainmentTrackingCategories {
	POINT_PACKAGE = "Point Package",
	CHANNELS_AND_STREAMING = "Kanaler og Streaming",
	PREMIUM_TV_CONTENT = "Premium TV Content",
}
